import type { LibraryTemplate } from "../library";
import {
  BLACK,
  captionBox,
  captions,
  during,
  ellipse,
  enter,
  logo,
  pill,
  rect,
  stack,
  star,
  TEXT_SHADOW,
  t,
  text,
  WHITE,
} from "./shorthands";

/**
 * Videos in the other formats: YouTube and websites (16:9, reference page
 * 1920 × 1080), feed (4:5, 1080 × 1350) and square (1080 × 1080). Like the
 * reels they can be created in every video format.
 */

const ON_VIDEO = { color: WHITE, shadow: TEXT_SHADOW };

const WIDE_CAPTIONS = captionBox("video-landscape");
const FEED_CAPTIONS = captionBox("video-feed");
const SQUARE_CAPTIONS = captionBox("video-square");

/** Small logo in the upper right corner of a 16:9 video. */
const wideLogo = () => logo({ x: 1580, y: 54, w: 286, h: 76, on: BLACK, align: "right" });

/** Name and role on a card with an accent bar (16:9), from the left or the right edge. */
function lowerThird(
  side: "left" | "right",
  y: number,
  name: string,
  role: string,
  start: number,
  end: number,
) {
  const width = 710;
  const x = side === "left" ? 54 : 1920 - 54 - width;
  const entrance = side === "left" ? "slide-right" : "slide-left";
  const motion = during(start, end, entrance, { duration: 500 });
  return [
    rect({
      x: side === "left" ? x : x + width - 14,
      y,
      w: 14,
      h: 156,
      fill: "accent",
      name: `Akzent ${name}`,
      ...motion,
    }),
    rect({
      x: side === "left" ? x + 14 : x,
      y,
      w: width - 14,
      h: 156,
      fill: "background/95",
      name: `Bauchbinde ${name}`,
      ...motion,
    }),
    stack({
      x: side === "left" ? x + 54 : x + 40,
      y: y + 78,
      w: width - 94,
      anchor: "center" as const,
      gap: 4,
      align: side,
      items: [
        t(name, "heading-2", 54, "text", motion),
        t(role, "caption", 32, "text-muted", motion),
      ],
    }),
  ];
}

export const WIDE_VIDEOS: LibraryTemplate[] = [
  // -------------------------------------------------------------------------
  // 16:9
  // -------------------------------------------------------------------------
  {
    id: "youtube-intro",
    title: "YouTube: Kanal-Intro",
    description: "Intro-Karte mit Logo, Kanalname und Streifen, danach Logo in der Ecke",
    presetId: "video-landscape",
    topic: "ankuendigung",
    style: "laut",
    tags: ["youtube", "intro", "kanal", "vorspann", "opener", "channel", "querformat"],
    pages: [
      {
        background: BLACK,
        nodes: [
          wideLogo(),
          captions(WIDE_CAPTIONS),
          rect({
            x: 0,
            y: 0,
            w: 1920,
            h: 1080,
            fill: "secondary",
            name: "Intro",
            ...during(0, 3500, "fade", { duration: 200, outDuration: 400 }),
          }),
          rect({
            x: 0,
            y: 708,
            w: 1920,
            h: 42,
            fill: "primary",
            name: "Streifen",
            ...during(0, 3500, "fly-left", { duration: 700, outDuration: 400 }),
          }),
          rect({
            x: 0,
            y: 750,
            w: 1920,
            h: 18,
            fill: "accent",
            name: "Streifen",
            ...during(0, 3500, "fly-right", { duration: 800, outDuration: 400 }),
          }),
          logo({
            x: 693,
            y: 230,
            w: 534,
            h: 178,
            on: "secondary",
            align: "center",
            ...during(0, 3500, "zoom", { duration: 600, outDuration: 400 }),
          }),
          stack({
            x: 250,
            y: 444,
            w: 1420,
            gap: 20,
            align: "center",
            items: [
              t(
                "Werkstatt-Wissen",
                "heading-1",
                124,
                "on:secondary",
                during(0, 3500, "slide-up", { duration: 700, outDuration: 400 }),
              ),
              t(
                "Jeden Dienstag ein neues Video",
                "subline",
                42,
                "accent",
                during(0, 3500, "slide-up", { duration: 900, outDuration: 400 }),
              ),
            ],
          }),
        ],
      },
    ],
  },
  {
    id: "youtube-interview",
    title: "YouTube: Interview mit Bauchbinden",
    description: "Zwei Bauchbinden nacheinander (links und rechts), Themen-Marke und Untertitel",
    presetId: "video-landscape",
    topic: "team",
    style: "elegant",
    tags: ["interview", "bauchbinde", "lower third", "gespräch", "name", "talk", "zwei personen"],
    previewTime: 1500,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(54, 54, 390, 78, "INTERVIEW", "primary", 32, enter("slide-down")),
          wideLogo(),
          ...lowerThird("left", 500, "Maxi Mustermann", "Geschäftsführerin", 800, 6800),
          ...lowerThird("right", 500, "Jonas Weber", "Leiter Entwicklung", 7500, 13500),
          captions(WIDE_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "youtube-vortrag",
    title: "YouTube: Vortrag & Webinar",
    description: "Titelkarte am Anfang, danach Vortragstitel oben links, Referentin und Untertitel",
    presetId: "video-landscape",
    topic: "event",
    style: "klar",
    tags: [
      "webinar",
      "vortrag",
      "präsentation",
      "aufzeichnung",
      "referent",
      "konferenz",
      "schulung",
    ],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 54,
            y: 54,
            w: 676,
            h: 84,
            fill: "background/92",
            radius: "sm",
            name: "Vortragstitel",
          }),
          text({
            text: "Social Media für kleine Unternehmen",
            style: "subline",
            size: 32,
            color: "text",
            x: 84,
            y: 54,
            w: 620,
            h: 84,
          }),
          wideLogo(),
          rect({
            x: 54,
            y: 500,
            w: 710,
            h: 152,
            fill: "primary",
            radius: "sm",
            name: "Referentin",
            ...during(4200, 10200, "slide-right", { duration: 500 }),
          }),
          stack({
            x: 96,
            y: 576,
            w: 640,
            anchor: "center",
            gap: 4,
            items: [
              t("Lena Berger", "heading-2", 50, "on:primary", during(4200, 10200, "slide-right")),
              t(
                "Marketing-Leitung, Beispiel GmbH",
                "caption",
                30,
                "on:primary/85",
                during(4200, 10200, "slide-right"),
              ),
            ],
          }),
          captions({ ...WIDE_CAPTIONS, look: "box" }),
          rect({
            x: 0,
            y: 0,
            w: 1920,
            h: 1080,
            fill: "primary",
            name: "Titelkarte",
            ...during(0, 4000, "fade", { duration: 200, outDuration: 500 }),
          }),
          stack({
            x: 214,
            y: 480,
            w: 1492,
            anchor: "center",
            gap: [24, 36],
            align: "center",
            items: [
              t("WEBINAR-AUFZEICHNUNG", "subline", 40, "on:primary/80", during(0, 4000, "fade")),
              t(
                "Social Media für kleine Unternehmen",
                "heading-1",
                104,
                "on:primary",
                during(0, 4000, "slide-up", { duration: 700 }),
              ),
              t(
                "mit Lena Berger · 14. November 2026",
                "body",
                42,
                "on:primary/90",
                during(0, 4000, "slide-up", { duration: 900 }),
              ),
            ],
          }),
          logo({
            x: 746,
            y: 698,
            w: 428,
            h: 100,
            on: "primary",
            align: "center",
            ...during(0, 4000, "fade", { duration: 700 }),
          }),
        ],
      },
    ],
  },
  {
    id: "youtube-erklaervideo",
    title: "YouTube: Erklärvideo mit Kapiteln",
    description: "Kapitel-Karte zum Start jedes Abschnitts, danach Kapitel-Marke oben links",
    presetId: "video-landscape",
    topic: "wissen",
    style: "natur",
    tags: ["erklärvideo", "kapitel", "tutorial", "anleitung", "schulung", "lernen", "abschnitte"],
    previewTime: 1200,
    pages: [
      {
        background: BLACK,
        nodes: [
          wideLogo(),
          ...["Die Grundlagen", "Schritt für Schritt", "Tipps für Profis"].flatMap(
            (chapter, index) => {
              const start = index * 15000;
              const card = during(start, start + 3000, "zoom", { duration: 450 });
              const label = during(start + 3000, start + 15000, "slide-right", { duration: 400 });
              return [
                rect({
                  x: 516,
                  y: 338,
                  w: 888,
                  h: 356,
                  fill: "background/95",
                  radius: "lg",
                  name: `Kapitel ${index + 1}`,
                  ...card,
                }),
                stack({
                  x: 570,
                  y: 516,
                  w: 780,
                  anchor: "center",
                  gap: 18,
                  align: "center",
                  items: [
                    t(`KAPITEL ${index + 1}`, "subline", 36, "primary", card),
                    t(chapter, "heading-2", 82, "text", card),
                  ],
                }),
                rect({
                  x: 54,
                  y: 54,
                  w: 570,
                  h: 82,
                  fill: "primary",
                  radius: "full",
                  name: `Kapitel-Marke ${index + 1}`,
                  ...label,
                }),
                text({
                  text: `${index + 1} · ${chapter}`,
                  style: "subline",
                  size: 32,
                  color: "on:primary",
                  x: 90,
                  y: 54,
                  w: 500,
                  h: 82,
                  ...label,
                }),
              ];
            },
          ),
          captions({ ...WIDE_CAPTIONS, look: "minimal" }),
        ],
      },
    ],
  },
  {
    id: "youtube-produkttest",
    title: "YouTube: Produkttest",
    description: "Produktname, Bewertung mit Sternen und Untertitel",
    presetId: "video-landscape",
    topic: "produkt",
    style: "verspielt",
    tags: ["review", "test", "produkttest", "bewertung", "unboxing", "vergleich", "sterne"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 54,
            y: 54,
            w: 746,
            h: 266,
            fill: "background/92",
            radius: "md",
            name: "Produkt",
            ...enter("slide-right", { duration: 600 }),
          }),
          stack({
            x: 96,
            y: 187,
            w: 676,
            anchor: "center",
            gap: 14,
            items: [
              t("IM TEST", "subline", 32, "primary", enter("slide-right", { duration: 600 })),
              t(
                "Akku-Bohrschrauber X2",
                "heading-2",
                60,
                "text",
                enter("slide-right", { duration: 600 }),
              ),
            ],
          }),
          ...[0, 1, 2, 3, 4].map((index) =>
            star({
              x: 60 + index * 78,
              y: 348,
              w: 64,
              h: 64,
              sides: 5,
              innerRatio: 0.45,
              fill: index < 4 ? "accent" : "#ffffff66",
              name: "Stern",
              ...enter("pop", { duration: 500 + index * 150 }),
            }),
          ),
          text({
            text: "4 von 5",
            style: "subline",
            size: 36,
            x: 462,
            y: 348,
            w: 338,
            h: 64,
            ...ON_VIDEO,
            ...enter("fade", { duration: 1300 }),
          }),
          wideLogo(),
          captions(WIDE_CAPTIONS),
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 4:5 feed
  // -------------------------------------------------------------------------
  {
    id: "feed-anzeige",
    title: "Feed-Video: Anzeige mit Button",
    description: "Schlagzeile oben, Nutzen darunter und ein pulsierender Button – mit Untertiteln",
    presetId: "video-feed",
    topic: "angebot",
    style: "klar",
    tags: ["anzeige", "werbung", "ad", "call to action", "kampagne", "facebook", "instagram"],
    pages: [
      {
        background: BLACK,
        nodes: [
          logo({ x: 60, y: 60, w: 260, h: 70, on: BLACK }),
          rect({
            x: 60,
            y: 160,
            w: 960,
            h: 230,
            fill: "primary",
            radius: "md",
            name: "Schlagzeile",
            ...enter("slide-down", { duration: 500 }),
          }),
          text({
            text: "Mehr Anfragen in 30 Tagen",
            style: "heading-1",
            size: 80,
            color: "on:primary",
            x: 100,
            y: 160,
            w: 880,
            h: 230,
            align: "center",
            ...enter("slide-down", { duration: 500 }),
          }),
          text({
            text: "Kostenloses Erstgespräch – ohne Risiko, ohne Vertrag",
            style: "subline",
            size: 34,
            x: 60,
            y: 404,
            w: 960,
            h: 96,
            align: "center",
            ...ON_VIDEO,
            ...enter("fade", { duration: 900 }),
          }),
          captions({ ...FEED_CAPTIONS, look: "box" }),
          ...pill(290, 530, 500, 100, "Jetzt Termin sichern", "accent", 34, {
            ...enter("pop", { duration: 1000, loop: "pulse" }),
          }),
        ],
      },
    ],
  },
  {
    id: "feed-titel-balken",
    title: "Feed-Video: Titel oben & unten",
    description:
      "Farbige Balken oben und unten mit Titel, Logo und Hinweis – das Video in der Mitte",
    presetId: "video-feed",
    topic: "ankuendigung",
    style: "warm",
    tags: ["balken", "letterbox", "rahmen", "titel", "news", "split", "banner"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 0,
            y: 0,
            w: 1080,
            h: 270,
            fill: "primary",
            name: "Balken oben",
            ...enter("slide-down", { duration: 500 }),
          }),
          stack({
            x: 60,
            y: 135,
            w: 960,
            anchor: "center",
            gap: 10,
            align: "center",
            items: [
              t(
                "NEU AB OKTOBER",
                "subline",
                28,
                "on:primary/80",
                enter("slide-down", { duration: 500 }),
              ),
              t(
                "Unser Herbstmenü ist da",
                "heading-1",
                76,
                "on:primary",
                enter("slide-down", { duration: 600 }),
              ),
            ],
          }),
          rect({
            x: 0,
            y: 1110,
            w: 1080,
            h: 240,
            fill: "primary",
            name: "Balken unten",
            ...enter("slide-up", { duration: 500 }),
          }),
          logo({
            x: 60,
            y: 1185,
            w: 320,
            h: 90,
            on: "primary",
            ...enter("slide-up", { duration: 500 }),
          }),
          text({
            text: "Jetzt probieren – nur im Oktober",
            style: "subline",
            size: 32,
            color: "on:primary",
            x: 400,
            y: 1185,
            w: 620,
            h: 90,
            align: "right",
            ...enter("slide-up", { duration: 600 }),
          }),
          captions({ x: 76, y: 860, w: 928, h: 216 }),
        ],
      },
    ],
  },
  {
    id: "feed-zitat",
    title: "Feed-Video: Zitat",
    description: "Abgedunkeltes Video mit großem Zitat, Name und Logo",
    presetId: "video-feed",
    topic: "zitat",
    style: "traditionell",
    tags: ["zitat", "spruch", "motivation", "gedanke", "weisheit", "quote"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({ x: 0, y: 0, w: 1080, h: 1350, fill: "#00000073", name: "Abdunklung" }),
          text({
            text: "“",
            style: "quote",
            size: 300,
            color: "accent",
            x: 80,
            y: 150,
            w: 300,
            ...enter("fade", { duration: 600 }),
          }),
          stack({
            x: 90,
            y: 680,
            w: 900,
            anchor: "center",
            gap: [36, 24],
            items: [
              t("Wer aufhört, besser zu werden, hat aufgehört, gut zu sein.", "quote", 74, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("fade", { duration: 1200 }),
              }),
              {
                kind: "shape",
                shape: "rect",
                w: 120,
                h: 6,
                fill: "accent",
                name: "Linie",
                ...enter("slide-right", { duration: 1400 }),
              },
              t("Philip Rosenthal", "subline", 34, WHITE, {
                shadow: TEXT_SHADOW,
                ...enter("fade", { duration: 1600 }),
              }),
            ],
          }),
          logo({ x: 60, y: 1200, w: 260, h: 80, on: BLACK }),
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 1:1
  // -------------------------------------------------------------------------
  {
    id: "quadrat-ankuendigung",
    title: "Quadrat-Video: Ankündigung",
    description: "Karte mit Datum und Titel, wackelnder „NEU“-Stern und Untertitel",
    presetId: "video-square",
    topic: "ankuendigung",
    style: "natur",
    tags: ["neu", "ankündigung", "eröffnung", "news", "quadrat", "neuigkeit"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 60,
            w: 700,
            h: 250,
            fill: "background/94",
            radius: "lg",
            name: "Karte",
            ...enter("slide-down", { duration: 600 }),
          }),
          stack({
            x: 100,
            y: 185,
            w: 620,
            anchor: "center",
            gap: 10,
            items: [
              t("AB 1. OKTOBER", "subline", 28, "primary", enter("slide-down", { duration: 600 })),
              t(
                "Wir eröffnen in der Altstadt",
                "heading-2",
                58,
                "text",
                enter("slide-down", { duration: 600 }),
              ),
            ],
          }),
          star({
            x: 790,
            y: 60,
            w: 230,
            h: 230,
            sides: 12,
            innerRatio: 0.8,
            rotation: -10,
            fill: "accent",
            name: "Stern",
            ...enter("zoom", { duration: 700, loop: "wiggle" }),
          }),
          text({
            text: "NEU",
            style: "heading-2",
            size: 60,
            color: "on:accent",
            x: 815,
            y: 60,
            w: 180,
            h: 230,
            rotation: -10,
            align: "center",
            ...enter("zoom", { duration: 700, loop: "wiggle" }),
          }),
          captions({ ...SQUARE_CAPTIONS, look: "box" }),
          logo({ x: 60, y: 990, w: 240, h: 56, on: BLACK }),
        ],
      },
    ],
  },
  {
    id: "quadrat-rueckblick",
    title: "Quadrat-Video: Event-Rückblick",
    description: "Intro-Karte „Rückblick“ in den ersten Sekunden, danach Event-Marke und Logo",
    presetId: "video-square",
    topic: "event",
    style: "verspielt",
    tags: ["rückblick", "highlights", "recap", "aftermovie", "event", "danke", "jahresrückblick"],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(60, 60, 420, 70, "Sommerfest 2026", "accent", 30, {
            ...during(2800, 15000, "slide-down", { duration: 500 }),
          }),
          logo({ x: 780, y: 62, w: 240, h: 66, on: BLACK, align: "right" }),
          rect({
            x: 0,
            y: 0,
            w: 1080,
            h: 1080,
            fill: "primary",
            name: "Intro",
            ...during(0, 2800, "fade", { duration: 200, outDuration: 400 }),
          }),
          ellipse({
            x: 790,
            y: 800,
            w: 460,
            h: 460,
            fill: "accent",
            name: "Kreis",
            ...during(0, 2800, "zoom", { duration: 700, outDuration: 400 }),
          }),
          stack({
            x: 90,
            y: 520,
            w: 900,
            anchor: "center",
            gap: [12, 24],
            items: [
              t("RÜCKBLICK", "subline", 36, "on:primary/80", during(0, 2800, "slide-up")),
              t(
                "Sommerfest 2026",
                "heading-1",
                120,
                "on:primary",
                during(0, 2800, "slide-up", { duration: 650 }),
              ),
              t(
                "Danke, dass ihr dabei wart!",
                "body",
                38,
                "on:primary/90",
                during(0, 2800, "slide-up", { duration: 800 }),
              ),
            ],
          }),
          logo({
            x: 90,
            y: 880,
            w: 300,
            h: 90,
            on: "primary",
            ...during(0, 2800, "fade", { duration: 700 }),
          }),
        ],
      },
    ],
  },
];
